import { ReactElement } from "react";
import { useLocation, Link } from "react-router-dom";
import { Typography } from "storybook/components/typography";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import { HeaderGraphic, HeaderGraphicWrapper } from "./header-graphic";
import { Logo } from "components/logo";
import { LanguagePicker } from "components/language-picker";
import { Button } from "storybook/components/button";
import { usePatientAuth } from "context/patient-auth";
import { useLocationContext } from "context/location";
import { preserveQueryParams } from "helpers";
import {
  Header,
  Content,
  LogoIconContainer,
  BackIconContainer,
  MainContainer,
  FooterContainer,
  InnerContainer,
  ContentContainer,
  GraphicContainer,
  SelectContainer,
} from "./account-layout-styles";
import { EmailGraphic } from "./email-graphic";
import { SecurityGraphic } from "./security-graphic";
import { SendCodeGraphic } from "./send-code-graphic";

type Props = {
  children: ReactElement;
};

const currentYear = new Date().getFullYear();

function AccountLayoutGraphic() {
  const location = useLocation();
  const locationContext = useLocationContext();

  const customLogoUrl = locationContext?.location?.patient_portal_logo?.large;
  const isLocationLoading = locationContext?.isLoading ?? false;

  const isConfirmPage = location.pathname.includes("/confirm");
  const isResendCodePage = location.pathname.includes("/code");
  const isMatchingPage = location.pathname.includes("/info");
  const isForgotPage = location.pathname.includes("/forgot");
  const isForgotConfirmPage = location.pathname.includes("/forgot/confirm");

  if (isMatchingPage) {
    return null;
  }

  if (isResendCodePage) {
    return (
      <GraphicContainer data-id="GraphicContainer">
        <SendCodeGraphic />
      </GraphicContainer>
    );
  }

  if (isConfirmPage) {
    return (
      <GraphicContainer>
        <EmailGraphic />
      </GraphicContainer>
    );
  }

  if (isForgotPage) {
    return (
      <GraphicContainer>
        {isForgotConfirmPage ? <EmailGraphic /> : <SecurityGraphic />}
      </GraphicContainer>
    );
  }

  if (isLocationLoading) {
    return <GraphicContainer data-id="GraphicContainer" />;
  }

  if (customLogoUrl) {
    return (
      <GraphicContainer data-id="GraphicContainer">
        <HeaderGraphicWrapper>
          <img src={customLogoUrl} alt="Organization logo" />
        </HeaderGraphicWrapper>
      </GraphicContainer>
    );
  }

  return (
    <GraphicContainer data-id="GraphicContainer">
      <HeaderGraphic />
    </GraphicContainer>
  );
}

function AccountLayoutHeader() {
  const location = useLocation();
  const { logout } = usePatientAuth();
  const isForgotPage = location.pathname.includes("/forgot");
  const isForgotConfirmPage = location.pathname.includes("/forgot/confirm");
  const isMatchingPage = location.pathname.includes("/info");

  if (isForgotPage && !isForgotConfirmPage) {
    return (
      <Link to={preserveQueryParams("/login/password")}>
        <BackIconContainer>
          <ChevronLeftIcon />
        </BackIconContainer>
      </Link>
    );
  }

  return (
    <>
      <LogoIconContainer>
        <Logo />
      </LogoIconContainer>
      <SelectContainer>
        <LanguagePicker />
      </SelectContainer>
      {isMatchingPage && (
        <Button
          variant="outlined"
          theme="default"
          size="small"
          onClick={logout}
        >
          Log out
        </Button>
      )}
    </>
  );
}

export function AccountLayout({ children }: Props) {
  return (
    <>
      <MainContainer>
        <ContentContainer>
          <InnerContainer>
            <Header>
              <AccountLayoutHeader />
            </Header>
            <AccountLayoutGraphic />
            <Content>{children}</Content>
          </InnerContainer>
        </ContentContainer>
      </MainContainer>
      <FooterContainer>
        <Typography
          theme="default"
          variant="subtitle1"
          color="ciqBrandLight"
          component="p"
        >
          © {currentYear} CancerIQ. All Rights Reserved.{" "}
          <a
            href="https://www.canceriq.com/terms-of-use-agreement"
            target="_blank"
          >
            Terms of Service
          </a>
        </Typography>
      </FooterContainer>
    </>
  );
}
